import React, { useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toaster } from 'rsuite'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { IFModal, IFFilter, IFButton, IFText, IFToastMessage } from 'Components'
import { stationApiService } from 'Services/ApiService'
import StationSelectors from 'Stores/Station/Selectors'
import { siteTransactionFilter } from 'Constants/Filters'
import IFFilterType from 'Enums/IFFilterType'
import { encodeObjectToBase64 } from 'Utils/base64functions'
import { Colors } from 'Theme'
import Styles from './SiteTransactionsModal.module.css'

const DEFAULT_RANGE_DAYS = 30

const buildDefaultFilter = () => {
  const end = new Date()
  end.setHours(23, 59, 59, 999)
  const start = new Date()
  start.setDate(start.getDate() - DEFAULT_RANGE_DAYS)
  start.setHours(0, 0, 0, 0)
  return siteTransactionFilter.map((f) =>
    f.type === IFFilterType.DATE_RANGE
      ? { ...f, value: [start, end], initialValue: [start, end] }
      : { ...f, value: [], initialValue: [] },
  )
}

const SiteTransactionsModal = React.forwardRef(({ station }, ref) => {
  const { t } = useTranslation()
  const modalRef = useRef()
  const filterRef = useRef()
  const [filter, setFilter] = useState(buildDefaultFilter)
  const [filterKey, setFilterKey] = useState(0)
  const [isDownloading, setIsDownloading] = useState(false)

  useImperativeHandle(ref, () => ({
    show: () => {
      setFilter(buildDefaultFilter())
      setFilterKey((k) => k + 1)
      modalRef.current?.show()
    },
    dismiss: () => modalRef.current?.dismiss(),
  }))

  const download = () => {
    if (!station) return
    setIsDownloading(true)
    stationApiService({
      url: `/${station._id}/transactions/report`,
      method: 'POST',
      data: {
        filter: encodeObjectToBase64(filter),
        fileType: 'excel',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      responseType: 'blob',
    })
      .then((res) => {
        setIsDownloading(false)
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const siteName = station.name
          ? station.name.replace(/\s+/g, '_')
          : station._id
        saveAs(blob, `Transactions_${siteName}_${moment().toISOString()}.xlsx`)
      })
      .catch((err) => {
        setIsDownloading(false)
        toaster.push(
          <IFToastMessage
            type="error"
            text={err.message ? err.message : err.statusText}
          />,
          { placement: 'topEnd' },
        )
      })
  }

  return (
    <IFModal
      ref={modalRef}
      maxWidth="sm"
      fullWidth
      title={t('SiteTransactionsModal.Title')}
      style={{ overflow: 'visible', maxWidth: '720px' }}
      dialogContentStyle={{ overflow: 'visible' }}
    >
      <div className={Styles.Wrapper}>
        <IFText className={Styles.Subtitle} style={{ color: Colors.gray }}>
          {t('SiteTransactionsModal.Subtitle', { site: station?.name ?? '' })}
        </IFText>
        <div className={Styles.FilterContainer}>
          <IFFilter
            ref={filterRef}
            key={`SiteTransactionsFilter-${filterKey}`}
            filters={filter}
            onFilterChange={(newFilter) => setFilter(newFilter)}
            textFieldPlaceholder={t('SiteTransactionsModal.FilterPlaceholder')}
            disableFutureDates={true}
          />
        </div>
        <div className={Styles.Actions}>
          <IFButton
            size="sm"
            isFill
            color={Colors.primary}
            text={t('SiteTransactionsModal.Download')}
            icon={<FileDownloadOutlinedIcon style={{ fontSize: 18 }} />}
            isLoading={isDownloading}
            onClick={download}
          />
        </div>
      </div>
    </IFModal>
  )
})

const mapStateToProps = (state) => ({
  station: StationSelectors.getSelectedStation(state),
})

SiteTransactionsModal.propTypes = {
  station: PropTypes.object,
}

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  SiteTransactionsModal,
)
